import { loadStripe, Stripe } from '@stripe/stripe-js';
import { StripeAccount } from 'hydra-data-types';
import { useMemo } from 'react';
import { isDefined } from 'ts-utils';
import { stripePublishableKey } from './StripePublishableKeys';

// Stripe Elements and stripe.confirmCardSetup require the same Stripe instance to be used.
const stripePromises: Partial<Record<StripeAccount, Promise<Stripe>>> = {};

export function stripeInitialize(account: StripeAccount): void {
  if (!isDefined(stripePromises[account])) {
    // Lazy initializing to prevent circular dependency initialization errors.
    stripePromises[account] = loadStripe(stripePublishableKey(account)).then((stripe) => {
      if (stripe === null) {
        throw new Error('Stripe.js loaded in a non-browser environment');
      }
      return stripe;
    });
  }
}

export async function stripeClient(account: StripeAccount): Promise<Stripe> {
  stripeInitialize(account);

  try {
    const stripePromise = stripePromises[account];
    if (!isDefined(stripePromise)) {
      throw new Error(`Stripe was not initialized for account '${account}'`);
    }
    return await stripePromise;
  } catch (cause) {
    const diagnostics = {
      stripeAccount: account,
      online: navigator.onLine,
      timeSincePageLoad: Math.round(performance.now()),
    };

    console.error('Stripe.js loading failed. Diagnostics:', diagnostics, cause);

    throw new Error(`Failed to load Stripe.js. Diagnostics: ${JSON.stringify(diagnostics)}`, {
      cause,
    });
  }
}

export function useStripeClient(account: StripeAccount): Promise<Stripe> {
  // Unlike useRef, this keeps the Promise stable for Stripe's provider while selecting a new
  // account-scoped client if a recreated Checkout Session moves between AU and US.
  return useMemo(() => stripeClient(account), [account]);
}
